import { motion } from "framer-motion";
import { Building2, Lightbulb, Handshake, ExternalLink, Linkedin, Mail } from "lucide-react";
import tpfcLogo from "@/assets/tpfc-logo.webp";
import qualitadoLogo from "@/assets/qualitado-logo.avif";
import christophImg from "@/assets/christoph-tegeler.webp";
import richardImg from "@/assets/richard-roensch.webp";
import { Card, CardContent } from "@/components/ui/card";

const milestones = [
  {
    icon: Building2,
    title: "Unternehmensnachfolge",
    desc: "Christoph Tegeler und Richard Rönsch haben zwei Unternehmen in Nachfolgesituationen übernommen und führen diese erfolgreich weiter – eine Personalberatung und einen Online-Händler.",
  },
  {
    icon: Lightbulb,
    title: "Eigene Pain Points gelöst",
    desc: "In beiden Unternehmen fielen täglich repetitive Aufgaben an: Im Recruiting die Kandidatensuche und -ansprache, im E-Commerce die Beantwortung von Kundenanfragen. Dafür haben wir eigene KI-Lösungen entwickelt.",
  },
  {
    icon: Handshake,
    title: "Von Unternehmern für Unternehmer",
    desc: "Unsere KI-Tools haben unsere eigene Effizienz um signifikant gesteigert. Jetzt machen wir diese Lösungen auch anderen Unternehmen zugänglich – praxiserprobt und aus erster Hand.",
  },
];

const companies = [
  {
    logo: tpfcLogo,
    name: "The People Finding Company GmbH",
    role: "Personalberatung – Ursprung von recruTen.AI",
    desc: "Spezialisierte Personalberatung für die passgenaue Besetzung von Fach- und Führungspositionen im Mittelstand. Hier entstand die Idee und der Prototyp von recruTen.AI, um den gesamten Recruiting-Prozess mit KI zu automatisieren.",
    url: "https://tpfcompany.de",
  },
  {
    logo: qualitadoLogo,
    name: "Qualitado GmbH",
    role: "Online-Handel – Ursprung von zenTen.AI",
    desc: "E-Commerce-Unternehmen mit Präsenz auf Amazon, Kaufland, Mediamarkt und weiteren Marktplätzen im DACH-Raum. Die tägliche Flut an Kundenanfragen führte zur Entwicklung von zenTen.AI für automatisierte, KI-gestützte Antworten.",
    url: "https://qualitado.de",
  },
];

const AboutSection = () => {
  return (
    <section id="ueber-uns" className="py-16 md:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Über uns
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Die Geschichte hinter successioAI – von eigenen Herausforderungen zu praxiserprobten KI-Lösungen
          </p>
        </motion.div>

        {/* Story milestones with connecting line */}
        <div className="relative grid md:grid-cols-3 gap-8 mb-14">
          {/* Connecting line on desktop */}
          <motion.div
            className="hidden md:block absolute top-[3.5rem] left-[16.67%] right-[16.67%] h-px bg-border"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          />
          {milestones.map((m, i) => (
            <motion.div
              key={m.title}
              className="relative text-center p-6 rounded-2xl bg-card border border-border shadow-sm transition-all duration-300 hover:shadow-md hover:border-primary/20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
            >
              <div className="w-14 h-14 rounded-2xl bg-primary/5 flex items-center justify-center mx-auto mb-5">
                <m.icon className="w-7 h-7 text-primary/60" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-3">{m.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Founders */}
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-foreground mb-6">Unsere Gründer</h3>
          <div className="grid sm:grid-cols-2 gap-8">
            {[
              { name: "Christoph Tegeler", role: "Geschäftsführer & Co-Founder", img: christophImg, linkedin: "https://www.linkedin.com/in/christoph-tegeler", email: "christoph.tegeler@successio.de" },
              { name: "Richard Rönsch", role: "Geschäftsführer & Co-Founder", img: richardImg, linkedin: "https://www.linkedin.com/company/successio-unternehmensnachfolge/about/", email: "richard.roentsch@successio.de" },
            ].map((founder) => (
              <div key={founder.name} className="p-6 rounded-2xl bg-card border border-border shadow-sm transition-all duration-300 hover:shadow-md hover:border-primary/20">
                <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4 ring-2 ring-primary/10">
                  <img src={founder.img} alt={founder.name} className="w-full h-full object-cover object-top" />
                </div>
                <h4 className="text-lg font-bold text-foreground">{founder.name}</h4>
                <p className="text-sm text-muted-foreground mb-3">{founder.role}</p>
                <div className="flex items-center justify-center gap-3">
                  <a href={founder.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground transition-colors">
                    <Linkedin className="w-4 h-4" />
                  </a>
                  <a href={`mailto:${founder.email}`} className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground transition-colors">
                    <Mail className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
          <p className="text-muted-foreground mt-6 leading-relaxed">
            Christoph und Richard haben über die Successio Unternehmensnachfolge GmbH zwei Unternehmen
            in Nachfolgesituationen übernommen. Aus den täglichen Herausforderungen in Recruiting und
            E-Commerce entwickelten sie eigene KI-Lösungen, die heute als recruTen.AI und zenTen.AI
            auch anderen Unternehmen zur Verfügung stehen.
          </p>
        </motion.div>

        {/* Companies */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">Unsere Unternehmen</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {companies.map((c, i) => (
              <motion.div
                key={c.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="h-full border-border shadow-sm transition-all duration-300 hover:shadow-md hover:border-primary/20">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl overflow-hidden mb-4">
                      <img src={c.logo} alt={c.name} className="w-full h-full object-contain" />
                    </div>
                    <h4 className="text-base font-bold text-foreground mb-1">{c.name}</h4>
                    <p className="text-xs font-semibold text-muted-foreground mb-3">{c.role}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">{c.desc}</p>
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:underline"
                    >
                      Website besuchen <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
